import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import DraftsIcon from '@mui/icons-material/DraftsOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import { apiClient } from '@/api/axiosClient';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { topN } from '@/lib/dashboardAggregations';
import type { InvoiceDto, VasChargeDto } from '@/types/billing';

const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function BillingSlide() {
  const { t } = useTranslation('dashboard');
  const colors = useCategoryColors();

  const { data: vasCharges = [] } = useQuery({
    queryKey: ['vas-charges'],
    queryFn: async () => (await apiClient.get<VasChargeDto[]>('/billing/vas-charges', { params: { status: 'Unbilled' } })).data,
    refetchInterval: 60_000,
  });
  const { data: invoices = [] } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => (await apiClient.get<InvoiceDto[]>('/billing/invoices')).data,
    refetchInterval: 60_000,
  });

  const draftInvoices = invoices.filter((i) => i.status === 'Draft');
  const now = new Date();
  const thisMonthBilled = invoices
    .filter((i) => i.status !== 'Draft' && new Date(i.invoiceDate).getMonth() === now.getMonth() && new Date(i.invoiceDate).getFullYear() === now.getFullYear())
    .reduce((sum, i) => sum + i.totalAmount, 0);
  const unpaidInvoices = invoices.filter((i) => i.status === 'Finalized' || i.status === 'Sent');

  const topUnpaid = topN(unpaidInvoices, (i) => i.totalAmount, 5);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('billingPanel.unbilledVas')} value={vasCharges.length} icon={<PendingActionsIcon />} accentColor={colors.billing} />
        <TvKpiTile label={t('billingPanel.draftInvoices')} value={draftInvoices.length} icon={<DraftsIcon />} accentColor={colors.billing} />
        <TvKpiTile label={t('billingPanel.thisMonthBilled')} value={formatCurrency(thisMonthBilled)} icon={<PaymentsIcon />} accentColor={colors.billing} />
        <TvKpiTile label={t('billingPanel.unpaidInvoices')} value={unpaidInvoices.length} icon={<ReceiptLongIcon />} accentColor={colors.billing} />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TvTopList
          title={t('billingPanel.topUnpaidTitle')}
          accentColor={colors.billing}
          items={topUnpaid.map((i) => ({ label: `${i.invoiceNumber} — ${i.clientName}`, value: i.totalAmount, formattedValue: formatCurrency(i.totalAmount) }))}
        />
      </Box>
    </Box>
  );
}
